// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const Register = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [referralInfo, setReferralInfo] = useState(null);
    const [validatingCode, setValidatingCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { register, clearError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // Проверка URL на наличие реферального кода
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refCode = params.get('ref');
        if (refCode) {
            setReferralCode(refCode);
            validateReferralCode(refCode);
        }
    }, [location]);

    // Функция для валидации реферального кода
    const validateReferralCode = async (code) => {
        if (!code) {
            setReferralInfo(null);
            return;
        }

        setValidatingCode(true);

        try {
            const response = await api.get(`/api/seller/validate-code/${code}`);
            if (response.data.success) {
                setReferralInfo(response.data.data);
            } else {
                setReferralInfo(null);
            }
        } catch (err) {
            console.error('Error validating referral code:', err);
            setReferralInfo(null);
        } finally {
            setValidatingCode(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Валидация
        if (!username || !password || !confirmPassword) {
            setError(t('auth.fillAllFields'));
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError(t('auth.passwordMismatch'));
            setLoading(false);
            return;
        }

        const result = await register(username, password, referralCode || undefined);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.message || t('common.error'));
        }

        setLoading(false);
    };

    return (
        <Container className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: 'calc(100vh - 200px)' }}>
            <div className="auth-form-container" style={{ position: 'relative', zIndex: 20 }}>
                <Card className="auth-card" style={{ position: 'relative', zIndex: 25 }}>
                    <Card.Body className="p-4">
                        <div className="text-center mb-4">
                            <div className="brand-logo justify-content-center mb-2">
                                <div className="brand-icon">
                                    <span className="brand-letter">H</span>
                                </div>
                            </div>
                            <h2 className="gradient-text">{t('auth.registerTitle')}</h2>
                            <p className="text-secondary">{t('auth.registerSubtitle')}</p>
                        </div>

                        {error && (
                            <Alert
                                variant="danger"
                                onClose={clearError}
                                dismissible
                            >
                                {error}
                            </Alert>
                        )}

                        {referralInfo && (
                            <Alert variant="info" className="mb-3">
                                <p className="mb-0">
                                    <strong>{t('referral.invitedBy')}:</strong> {referralInfo.sellerName}
                                    <br />
                                    <strong>{t('referral.linkName')}:</strong> {referralInfo.name}
                                </p>
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 30 }}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t('auth.username')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('auth.enterUsername')}
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="web3-input"
                                    style={{ position: 'relative', zIndex: 30 }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t('auth.password')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder={t('auth.enterPassword')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="web3-input"
                                    style={{ position: 'relative', zIndex: 30 }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t('auth.confirmPassword')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder={t('auth.repeatPassword')}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="web3-input"
                                    style={{ position: 'relative', zIndex: 30 }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="text-secondary">{t('referral.code')}</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('referral.enterCode')}
                                        value={referralCode}
                                        onChange={(e) => {
                                            setReferralCode(e.target.value);
                                            if (!e.target.value) {
                                                setReferralInfo(null);
                                            }
                                        }}
                                        className="web3-input"
                                        style={{ position: 'relative', zIndex: 30 }}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => validateReferralCode(referralCode)}
                                        disabled={validatingCode || !referralCode}
                                    >
                                        {validatingCode ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            t('common.check')
                                        )}
                                    </Button>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    {t('referral.codeInfo')}
                                </Form.Text>
                            </Form.Group>

                            <Button
                                variant="primary"
                                type="submit"
                                className="w-100 py-2"
                                disabled={loading}
                                style={{ position: 'relative', zIndex: 30 }}
                            >
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {t('auth.registerProcess')}
                                    </>
                                ) : t('auth.registerButton')}
                            </Button>
                        </Form>
                        <div className="text-center mt-4">
                            <p className="text-secondary">
                                {t('auth.haveAccount')} <Link to="/login" className="gradient-text">{t('common.login')}</Link>
                            </p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </Container>
    );
};

export default Register;